import { useState, useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Upload, Loader2, Trash2, FileSpreadsheet, Clock, CheckCircle2, XCircle, RefreshCw } from "lucide-react";
import SyncUploadDialog from "./SyncUploadDialog";

interface ManualUpload {
  id: string;
  file_name: string;
  file_path: string;
  file_size: number | null;
  file_type: string | null;
  sync_status: string;
  table_name: string | null;
  row_count: number | null;
  error_message: string | null;
  created_at: string;
}

const statusConfig: Record<string, { label: string; variant: "default" | "secondary" | "destructive"; icon: React.ElementType }> = {
  pending: { label: "Pending", variant: "secondary", icon: Clock },
  synced: { label: "Synced", variant: "default", icon: CheckCircle2 },
  failed: { label: "Failed", variant: "destructive", icon: XCircle },
};

const ACCEPTED_TYPES = ".csv,.xls,.xlsx";

const ManualUploads = () => {
  const { user, profile } = useAuth();
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploads, setUploads] = useState<ManualUpload[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [syncUpload, setSyncUpload] = useState<ManualUpload | null>(null);

  const fetchUploads = async () => {
    const { data, error } = await supabase
      .from("manual_upload")
      .select("*")
      .order("created_at", { ascending: false });
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      setUploads((data as unknown as ManualUpload[]) || []);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchUploads();
  }, []);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user || !profile) return;

    const ext = file.name.split(".").pop()?.toLowerCase();
    if (!["csv", "xls", "xlsx"].includes(ext || "")) {
      toast({ title: "Invalid file", description: "Only CSV, XLS, and XLSX files are accepted.", variant: "destructive" });
      return;
    }

    setUploading(true);
    try {
      const filePath = `${profile.client_id}/${Date.now()}_${file.name}`;

      const { error: storageError } = await supabase.storage
        .from("manual-uploads")
        .upload(filePath, file);

      if (storageError) throw storageError;

      const { error: dbError } = await supabase
        .from("manual_upload")
        .insert({
          file_name: file.name,
          file_path: filePath,
          file_size: file.size,
          file_type: ext || null,
          sync_status: "pending",
          client_id: profile.client_id,
        } as any);

      if (dbError) throw dbError;

      toast({ title: "Uploaded", description: `"${file.name}" uploaded successfully.` });
      fetchUploads();
    } catch (err: any) {
      toast({ title: "Upload failed", description: err.message, variant: "destructive" });
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    const upload = uploads.find((u) => u.id === deleteId);
    if (!upload) return;

    // Delete from storage first
    await supabase.storage.from("manual-uploads").remove([upload.file_path]);
    // Delete from table
    const { error } = await supabase.from("manual_upload").delete().eq("id", deleteId);
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Deleted", description: "File removed." });
      fetchUploads();
    }
    setDeleteId(null);
  };

  const formatSize = (bytes: number | null) => {
    if (!bytes) return "—";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  if (loading) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col gap-6 p-6 overflow-y-auto">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Manual Uploads</h1>
          <p className="text-sm text-muted-foreground">Upload CSV or Excel files to import data.</p>
        </div>
        <div>
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_TYPES}
            className="hidden"
            onChange={handleFileSelect}
          />
          <Button onClick={() => fileInputRef.current?.click()} disabled={uploading}>
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            Upload File
          </Button>
        </div>
      </div>

      {uploads.length === 0 ? (
        <Card className="flex flex-col items-center justify-center py-12">
          <FileSpreadsheet className="h-12 w-12 text-muted-foreground mb-4" />
          <CardTitle className="text-lg mb-1">No files uploaded</CardTitle>
          <CardDescription>Upload a CSV or Excel file to get started.</CardDescription>
        </Card>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>File Name</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Size</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Uploaded</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {uploads.map((upload) => {
                const status = statusConfig[upload.sync_status] || statusConfig.pending;
                const StatusIcon = status.icon;
                return (
                  <TableRow key={upload.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <FileSpreadsheet className="h-4 w-4 text-muted-foreground shrink-0" />
                        <span className="font-medium truncate max-w-[200px]">{upload.file_name}</span>
                      </div>
                    </TableCell>
                    <TableCell className="uppercase text-xs">{upload.file_type || "—"}</TableCell>
                    <TableCell className="text-xs">{formatSize(upload.file_size)}</TableCell>
                    <TableCell>
                      <Badge variant={status.variant} className="gap-1">
                        <StatusIcon className="h-3 w-3" />
                        {status.label}
                      </Badge>
                      {upload.error_message && (
                        <div className="text-xs text-destructive mt-1">{upload.error_message}</div>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {new Date(upload.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-right flex items-center justify-end gap-1">
                      {upload.sync_status !== "synced" && (
                        <Button variant="ghost" size="icon" onClick={() => setSyncUpload(upload)} title="Sync to table">
                          <RefreshCw className="h-4 w-4" />
                        </Button>
                      )}
                      <Button variant="ghost" size="icon" onClick={() => setDeleteId(upload.id)} title="Delete">
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Card>
      )}

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete upload?</AlertDialogTitle>
            <AlertDialogDescription>This will permanently remove the file and its record.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <SyncUploadDialog
        open={!!syncUpload}
        onOpenChange={(o) => { if (!o) setSyncUpload(null); }}
        upload={syncUpload}
        onSyncStarted={fetchUploads}
      />
    </div>
  );
};

export default ManualUploads;
